/* eslint-disable no-unused-vars */
import { defer, of, throwError } from "rxjs";
import { catchError, take } from "rxjs";

import { poll } from "@/operators/poll.operator";
import ApiServices from "@/services/ApiServices";
import { map } from "rxjs";
import { ajax } from "rxjs/ajax";
import SecureLS from "secure-ls";
const ls = new SecureLS({
  isCompression: false,
});

let shouldError = false;
function fetchData(apiCall) {
  return defer(() => (shouldError ? throwError(null) : apiCall)).pipe(
    catchError(() => of({ response: "Fallback todo" }))
  );
}

const state = {
  transactions: [],
  members: [],
  transactionStatistics: [],
  msisdnStatistics: [],
  msisdnLifecycle: [],
  transactionAnalytics: {},
  topMsisdns: [],
  successFailedByMsisdn: [],
  totalCount: {
    membersCount: 0,
    staffCount: 0,
    totalMembersToday: 0,
    totalTransactions: 0,
  },
  paginate: {
    total: 0,
    page: 1,
    size: 30,
  },
  transactionStatisticsPagination: {
    page: 1,
    size: 10,
    totalRecords: 0,
    totalPages: 0,
  },
  successFailedByMsisdnPagination: {
    page: 1,
    size: 10,
    totalRecords: 0,
    totalPages: 0,
  },
};

const mutations = {
  UPDATE_TRANSACTIONS(state, payload) {
    state.transactions = payload;
  },
  UPDATE_MEMBERS(state, payload) {
    state.members = payload;
  },
  UPDATE_TRANSACTION_STATISTICS(state, payload) {
    state.transactionStatistics = payload;
  },
  UPDATE_MSISDN_STATISTICS(state, payload) {
    state.msisdnStatistics = payload;
  },
  UPDATE_MSISDN_LIFECYCLE(state, payload) {
    state.msisdnLifecycle = payload;
  },
  UPDATE_TRANSACTION_ANALYTICS(state, payload) {
    state.transactionAnalytics = payload;
  },
  UPDATE_TOP_MSISDNS(state, payload) {
    state.topMsisdns = payload;
  },
  UPDATE_SUCCESS_FAILED_BY_MSISDN(state, payload) {
    state.successFailedByMsisdn = payload;
  },
  UPDATE_COUNT(state, payload) {
    state.totalCount = payload;
  },
  UPDATE_PAGINATION(state, payload) {
    state.paginate = payload;
  },
  UPDATE_TRANSACTION_STATISTICS_PAGINATION(state, payload) {
    state.transactionStatisticsPagination = payload;
  },
  UPDATE_SUCCESS_FAILED_BY_MSISDN_PAGINATION(state, payload) {
    state.successFailedByMsisdnPagination = payload;
  },
};

const actions = {
  async newlyAddedMembers({ dispatch, commit }, payload) {
    try {
      const { page, size } = payload;
      dispatch("isPageLoading", true, { root: true });
      const query = `?page=${page}&size=${size}&newlyAdded=true`;
      const response$ = fetchData(ApiServices.getNewlyAddedMembers(query)).pipe(
        poll(8000)
      );
      response$.subscribe((response) => {
        dispatch("isPageLoading", false, { root: true });
        const { docs, totalDocs, limit, page } = response.data ?? {
          docs: [],
          totalDocs: 0,
          limit: 0,
          page: 0,
        };
        commit("UPDATE_MEMBERS", docs);
        commit("UPDATE_PAGINATION", { total: totalDocs, size: limit, page });
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response.data?.message ??
            "An error occurred while authenticating user",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async totalCount({ dispatch, commit }) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const session = JSON.parse(ls.get("cloud.access"))?.auth?.user?.token;
      const response$ = fetchData(
        ajax({
          url: `${process.env.VUE_APP_BASE_URL}/api/v1/reports/total/list`,
          headers: {
            authorization: `Bearer ${session}`,
          },
        })
      ).pipe(poll(8000));
      response$.subscribe((count) => {
        dispatch("isPageLoading", false, { root: true });
        commit(
          "UPDATE_COUNT",
          count?.response?.data ?? {
            membersCount: 0,
            staffCount: 0,
            totalMembersToday: 0,
            totalTransactions: 0,
          }
        );
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while authenticating user",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async getTransactionStatisticsByDate({ dispatch, commit }, payload) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const { startDate, endDate, page = 1, size = 10 } = payload || {};
      let query = `?page=${page}&size=${size}`;
      if (startDate && endDate) {
        query += `&startDate=${startDate}&endDate=${endDate}`;
      }
      const response$ = of(
        await ApiServices.getTransactionStatisticsByDate(query)
      ).pipe(map((response) => response.data));
      response$.subscribe((response) => {
        dispatch("isPageLoading", false, { root: true });
        commit("UPDATE_TRANSACTION_STATISTICS", response.data || []);
        if (response.pagination) {
          commit(
            "UPDATE_TRANSACTION_STATISTICS_PAGINATION",
            response.pagination
          );
        }
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while fetching transaction statistics",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async getMsisdnStatistics({ dispatch, commit }, payload) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const { startDate, endDate } = payload || {};
      const query =
        startDate && endDate
          ? `?startDate=${startDate}&endDate=${endDate}`
          : "";
      const response$ = of(await ApiServices.getMsisdnStatistics(query)).pipe(
        map((response) => response.data)
      );
      response$.subscribe((statistics) => {
        dispatch("isPageLoading", false, { root: true });
        commit("UPDATE_MSISDN_STATISTICS", statistics.data || []);
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while fetching MSISDN statistics",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async getTransactionAnalytics({ dispatch, commit }, payload) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const { startDate, endDate } = payload || {};
      const query =
        startDate && endDate
          ? `?startDate=${startDate}&endDate=${endDate}`
          : "";
      const response$ = of(
        await ApiServices.getTransactionAnalytics(query)
      ).pipe(map((response) => response.data));
      response$.subscribe((res) => {
        dispatch("isPageLoading", false, { root: true });
        commit("UPDATE_TRANSACTION_ANALYTICS", res.data || {});
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while fetching transaction analytics",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async getMsisdnLifecycle({ dispatch, commit }, payload) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const { startDate, endDate } = payload || {};
      const query =
        startDate && endDate
          ? `?startDate=${startDate}&endDate=${endDate}`
          : "";
      const response$ = of(await ApiServices.getMsisdnLifecycle(query)).pipe(
        map((response) => response.data)
      );
      response$.subscribe((res) => {
        dispatch("isPageLoading", false, { root: true });
        commit("UPDATE_MSISDN_LIFECYCLE", res.data || []);
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while fetching MSISDN lifecycle",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async getTopMsisdns({ dispatch, commit }, payload) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const { startDate, endDate, limit } = payload || {};
      let query = "";
      if (startDate && endDate) {
        query = `?startDate=${startDate}&endDate=${endDate}`;
        if (limit) {
          query += `&limit=${limit}`;
        }
      } else if (limit) {
        query = `?limit=${limit}`;
      }
      const response$ = of(await ApiServices.getTopMsisdns(query)).pipe(
        map((response) => response.data)
      );
      response$.subscribe((topMsisdns) => {
        dispatch("isPageLoading", false, { root: true });
        commit("UPDATE_TOP_MSISDNS", topMsisdns.data || []);
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while fetching top MSISDNs",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
  async getSuccessFailedByMsisdn({ dispatch, commit }, payload) {
    try {
      dispatch("isPageLoading", true, { root: true });
      const { startDate, endDate, page = 1, size = 10 } = payload || {};
      let query = `?page=${page}&size=${size}`;
      if (startDate && endDate) {
        query += `&startDate=${startDate}&endDate=${endDate}`;
      }
      const response$ = of(
        await ApiServices.getSuccessFailedByMsisdn(query)
      ).pipe(map((response) => response.data));
      response$.subscribe((response) => {
        dispatch("isPageLoading", false, { root: true });
        commit("UPDATE_SUCCESS_FAILED_BY_MSISDN", response.data || []);
        if (response.pagination) {
          commit(
            "UPDATE_SUCCESS_FAILED_BY_MSISDN_PAGINATION",
            response.pagination
          );
        }
      });
    } catch (e) {
      dispatch(
        "showSnackBar",
        {
          snackBar: true,
          message:
            e?.response?.data?.message ??
            "An error occurred while fetching success/failed statistics",
          status: "red",
        },
        { root: true }
      );
      dispatch("isPageLoading", false, { root: true });
    }
  },
};

const getters = {
  getTotalCount: (state) => state.totalCount,
  getNewMembers: (state) => state.members,
  getTransactionStatistics: (state) => state.transactionStatistics,
  getMsisdnStatistics: (state) => state.msisdnStatistics,
  getMsisdnLifecycle: (state) => state.msisdnLifecycle,
  getTransactionAnalytics: (state) => state.transactionAnalytics,
  getTopMsisdns: (state) => state.topMsisdns,
  getSuccessFailedByMsisdn: (state) => state.successFailedByMsisdn,
  getPagination: (state) => state.paginate,
  getTransactionStatisticsPagination: (state) =>
    state.transactionStatisticsPagination,
  getSuccessFailedByMsisdnPagination: (state) =>
    state.successFailedByMsisdnPagination,
};

export const reports = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
